import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAlertNotifyStore } from '@/alerts/stores/use-alert-notify-store';

type AuthContextType = {
  signIn: (refreshToken: string, accessToken: string) => void;
  signOut: () => void;
  refreshToken: string | null;
  accessToken: string | null;
  setAccessToken: (token: string) => void;
  isLoading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function useSession() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load tokens from localStorage on mount
  useEffect(() => {
    console.log('[SessionProvider] Initializing - loading tokens from localStorage...');

    const storedRefreshToken = localStorage.getItem('maestro-refresh-token');
    const storedAccessToken = localStorage.getItem('maestro-access-token');

    console.log('[SessionProvider] Found tokens:', {
      hasRefreshToken: !!storedRefreshToken,
      hasAccessToken: !!storedAccessToken,
      refreshTokenLength: storedRefreshToken?.length,
      accessTokenLength: storedAccessToken?.length,
    });

    if (storedRefreshToken) {
      setRefreshToken(storedRefreshToken);
    }
    if (storedAccessToken) {
      setAccessToken(storedAccessToken);
    }

    setIsLoading(false);
    console.log('[SessionProvider] Initialization complete');
  }, []);

  const signIn = (newRefreshToken: string, newAccessToken: string) => {
    console.log('[SessionProvider] signIn called', {
      hasRefreshToken: !!newRefreshToken,
      hasAccessToken: !!newAccessToken,
    });

    // Save to localStorage
    localStorage.setItem('maestro-refresh-token', newRefreshToken);
    localStorage.setItem('maestro-access-token', newAccessToken);

    // Update state
    setRefreshToken(newRefreshToken);
    setAccessToken(newAccessToken);

    console.log('[SessionProvider] Tokens saved to localStorage and state');
  };

  const signOut = () => {
    console.log('[SessionProvider] signOut called');

    localStorage.removeItem('maestro-refresh-token');
    localStorage.removeItem('maestro-access-token');
    setRefreshToken(null);
    setAccessToken(null);
    // Wipe persisted alert notifications so they never leak to the next user on
    // a shared device.
    useAlertNotifyStore.getState().reset();

    console.log('[SessionProvider] Tokens cleared from localStorage and state');
  };

  const updateAccessToken = (token: string) => {
    console.log('[SessionProvider] updateAccessToken called');

    localStorage.setItem('maestro-access-token', token);
    setAccessToken(token);

    console.log('[SessionProvider] Access token updated');
  };

  return (
    <AuthContext.Provider
      value={{
        signIn,
        signOut,
        refreshToken,
        accessToken,
        setAccessToken: updateAccessToken,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default SessionProvider;